/**
 * PerformanceTest.jsx
 * Dev-only admin page for benchmarking middleware sign/verify/forward throughput.
 *
 * Routing: Add to App.jsx as:
 *   <Route path="/perf" element={<PerformanceTest />} />
 *
 * Nav entry is injected into Layout.jsx conditionally — see Layout changes below.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { perfTestApi } from '../services/api';
import {
  Zap, Play, RotateCcw, Clock, TrendingUp,
  CheckCircle, XCircle, AlertTriangle, Server,
  ChevronDown, ChevronUp, Activity, BarChart2,
  Cpu, Radio, Key
} from 'lucide-react';

// ── Utility helpers ──────────────────────────────────────────────────────────

const fmt = {
  ms:  (v) => v == null ? '—' : `${Number(v).toFixed(3)} ms`,
  rps: (v) => v == null ? '—' : `${Number(v).toFixed(2)} rps`,
  num: (v) => v == null ? '—' : Number(v).toLocaleString(),
  dur: (ms) => {
    if (ms == null) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  },
  pct: (ok, total) => total === 0 ? '0%' : `${((ok / total) * 100).toFixed(1)}%`,
  ts:  (iso) => iso ? new Date(iso).toLocaleTimeString() : '—',
};

const OP_META = {
  SIGN:    { label: 'Sign',           color: '#6366f1', icon: Zap,         desc: 'Measure JWS RS256 signature generation throughput' },
  VERIFY:  { label: 'Verify',         color: '#10b981', icon: CheckCircle,  desc: 'Measure signature verification throughput (pre-signed payload)' },
  FORWARD: { label: 'Sign + Forward', color: '#f59e0b', icon: Radio,        desc: 'End-to-end: sign then PUT to switch simulator' },
};

const STATUS_BADGE = {
  RUNNING:   { bg: 'bg-blue-100',  text: 'text-blue-700',  label: 'Running'   },
  COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
  FAILED:    { bg: 'bg-red-100',   text: 'text-red-700',   label: 'Failed'    },
};

function Badge({ status }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.FAILED;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
      {status === 'RUNNING' && <span className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />}
      {s.label}
    </span>
  );
}

// ── Latency bar chart (pure CSS, no dependency) ───────────────────────────────

function LatencyBar({ label, value, max, color }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <span className="w-10 text-xs text-gray-500 font-mono text-right shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
      <span className="w-24 text-xs font-mono text-right text-gray-700 shrink-0">{fmt.ms(value)}</span>
    </div>
  );
}

function LatencyChart({ result }) {
  const { latencyMinMs, latencyMeanMs, latencyP50Ms, latencyP95Ms, latencyP99Ms, latencyMaxMs } = result;
  const opColor = OP_META[result.operation]?.color ?? '#6366f1';
  const maxVal  = latencyMaxMs ?? 0;

  const bars = [
    { label: 'min',  value: latencyMinMs  },
    { label: 'mean', value: latencyMeanMs },
    { label: 'p50',  value: latencyP50Ms  },
    { label: 'p95',  value: latencyP95Ms  },
    { label: 'p99',  value: latencyP99Ms  },
    { label: 'max',  value: latencyMaxMs  },
  ];

  return (
    <div className="space-y-2 py-2">
      {bars.map(b => (
        <LatencyBar key={b.label} label={b.label} value={b.value} max={maxVal} color={opColor} />
      ))}
    </div>
  );
}

// ── Result card ───────────────────────────────────────────────────────────────

function ResultCard({ result, isLatest }) {
  const [expanded, setExpanded] = useState(isLatest);
  const meta  = OP_META[result.operation] ?? OP_META.SIGN;
  const OpIcon = meta.icon;
  const total = result.successful + result.failed;

  return (
    <div className={`border rounded-xl overflow-hidden transition-shadow ${isLatest ? 'shadow-md border-indigo-200' : 'border-gray-200'}`}>
      {/* Header row */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center gap-3 px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
             style={{ backgroundColor: `${meta.color}18` }}>
          <OpIcon className="w-4 h-4" style={{ color: meta.color }} />
        </div>

        <div className="flex-1 text-left min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">{meta.label}</span>
            <Badge status={result.status} />
            {isLatest && (
              <span className="text-xs bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded font-medium">Latest</span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5 font-mono truncate">
            {result.concurrency}T × {result.iterations} iter  ·  started {fmt.ts(result.startedAt)}
          </div>
        </div>

        <div className="flex items-center gap-6 shrink-0">
          {result.status === 'COMPLETED' && (
            <>
              <div className="text-right hidden sm:block">
                <div className="text-sm font-bold text-gray-900">{fmt.rps(result.throughputRps)}</div>
                <div className="text-xs text-gray-400">throughput</div>
              </div>
              <div className="text-right hidden md:block">
                <div className="text-sm font-bold text-gray-900">{fmt.ms(result.latencyP99Ms)}</div>
                <div className="text-xs text-gray-400">p99</div>
              </div>
              <div className="text-right hidden md:block">
                <div className="text-sm font-bold text-gray-900">{fmt.pct(result.successful, total)}</div>
                <div className="text-xs text-gray-400">success</div>
              </div>
            </>
          )}
          {result.status === 'RUNNING' && (
            <Activity className="w-4 h-4 text-blue-500 animate-pulse" />
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-4 grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* Stats grid */}
          <div>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Run Summary</div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Throughput',    value: fmt.rps(result.throughputRps) },
                { label: 'Duration',      value: fmt.dur(result.totalDurationMs) },
                { label: 'Successful',    value: fmt.num(result.successful) },
                { label: 'Failed',        value: fmt.num(result.failed), warn: result.failed > 0 },
                { label: 'Concurrency',   value: `${result.concurrency} threads` },
                { label: 'Iterations',    value: fmt.num(result.iterations) },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className={`text-sm font-bold ${s.warn && result.failed > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                    {s.value}
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Forward-specific */}
            {result.operation === 'FORWARD' && result.switchUrl && (
              <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-xs font-semibold text-amber-700 mb-1">Switch Simulator</div>
                <div className="text-xs font-mono text-amber-800 break-all">{result.switchUrl}</div>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="text-green-700 font-semibold">✓ {fmt.num(result.switchSuccess)} accepted</span>
                  <span className="text-red-600 font-semibold">✗ {fmt.num(result.switchFailed)} rejected</span>
                </div>
              </div>
            )}

            {result.errorMessage && (
              <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-mono">
                {result.errorMessage}
              </div>
            )}
          </div>

          {/* Latency chart */}
          {result.status === 'COMPLETED' && result.latencyMinMs != null && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Latency Distribution</div>
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <LatencyChart result={result} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                {[
                  { label: 'p50 (median)', value: result.latencyP50Ms },
                  { label: 'p95',          value: result.latencyP95Ms },
                  { label: 'p99',          value: result.latencyP99Ms },
                ].map(p => (
                  <div key={p.label} className="bg-white border border-gray-200 rounded-lg p-2">
                    <div className="text-sm font-bold text-gray-900">{fmt.ms(p.value)}</div>
                    <div className="text-xs text-gray-400">{p.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.status === 'RUNNING' && (
            <div className="flex items-center justify-center h-32 text-sm text-gray-400">
              <Activity className="w-4 h-4 mr-2 animate-pulse text-blue-500" />
              Test in progress…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page component ───────────────────────────────────────────────────────

const DEFAULT_FORM = {
  operation:        'SIGN',
  concurrency:      4,
  iterations:       200,
  warmupIterations: 20,
};

export default function PerformanceTest() {
  const [config,      setConfig]      = useState(null);
  const [configError, setConfigError] = useState(null);
  const [form,        setForm]        = useState(DEFAULT_FORM);
  const [results,     setResults]     = useState([]);
  const [activeRunId, setActiveRunId] = useState(null);
  const [running,     setRunning]     = useState(false);
  const [error,       setError]       = useState(null);
  const pollRef = useRef(null);

  // ── Load config + past results on mount ────────────────────────────────
  useEffect(() => {
    perfTestApi.getConfig()
      .then(cfg => {
        setConfig(cfg);
        return perfTestApi.listResults(20);
      })
      .then(r => setResults(r))
      .catch(err => setConfigError(err.message ?? 'Failed to load perf test config'));
  }, []);

  // ── Poll for active run ────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => {
    if (!activeRunId) return;
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const r = await perfTestApi.getResult(activeRunId);
        setResults(prev => {
          const idx = prev.findIndex(x => x.runId === r.runId);
          if (idx >= 0) { const next = [...prev]; next[idx] = r; return next; }
          return [r, ...prev];
        });
        if (r.status !== 'RUNNING') {
          stopPolling();
          setRunning(false);
          setActiveRunId(null);
        }
      } catch (e) {
        stopPolling();
        setRunning(false);
        setActiveRunId(null);
      }
    }, 1500);
    return stopPolling;
  }, [activeRunId, stopPolling]);

  // ── Form submit ────────────────────────────────────────────────────────
  const handleRun = async () => {
    setError(null);
    setRunning(true);
    try {
      const { runId } = await perfTestApi.startRun(form);
      setActiveRunId(runId);
      // Optimistically insert a RUNNING placeholder
      setResults(prev => [{
        runId,
        operation:        form.operation,
        status:           'RUNNING',
        concurrency:      form.concurrency,
        iterations:       form.iterations,
        warmupIterations: form.warmupIterations,
        startedAt:        new Date().toISOString(),
      }, ...prev]);
    } catch (e) {
      setError(e.response?.data?.message ?? e.message ?? 'Failed to start test');
      setRunning(false);
    }
  };

  const field = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // ── Render ─────────────────────────────────────────────────────────────
  if (configError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">{configError}</p>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const maxConc = config.maxConcurrency ?? 50;
  const maxIter = config.maxIterations  ?? 10000;

  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Zap className="w-4 h-4 text-indigo-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Performance Testing</h1>
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">DEV ONLY</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Benchmark JWS sign, verify, and forward throughput against the middleware and switch simulator.
          </p>
        </div>

        {/* Context pills: simulator URL + signing identity */}
        <div className="flex items-center gap-2 flex-wrap">
          {config.fspId && (
            <div className="flex items-center gap-2 text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
              <Key className="w-3.5 h-3.5 shrink-0" />
              <span className="font-mono font-semibold">{config.fspId}</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-xs text-gray-500 bg-gray-100 rounded-lg px-3 py-2">
            <Server className="w-3.5 h-3.5 shrink-0" />
            <span className="font-mono truncate max-w-xs">{config.simulatorUrl}</span>
          </div>
        </div>
      </div>

      {/* Config form */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
        <div className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Cpu className="w-4 h-4 text-indigo-500" />
          Test Configuration
        </div>

        {/* Operation selector */}
        <div className="mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-2">Operation</label>
          <div className="grid grid-cols-3 gap-3">
            {Object.entries(OP_META).map(([op, meta]) => {
              const Icon = meta.icon;
              const active = form.operation === op;
              return (
                <button
                  key={op}
                  onClick={() => field('operation', op)}
                  disabled={running}
                  className={`flex flex-col items-start p-3 rounded-xl border-2 text-left transition-all ${
                    active
                      ? 'border-indigo-400 bg-indigo-50'
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4 shrink-0" style={{ color: active ? meta.color : '#9ca3af' }} />
                    <span className={`text-sm font-semibold ${active ? 'text-indigo-700' : 'text-gray-700'}`}>
                      {meta.label}
                    </span>
                  </div>
                  <span className="text-xs text-gray-400 leading-relaxed">{meta.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Numeric inputs */}
        <div className="grid grid-cols-3 gap-4 mb-5">
          {[
            {
              key: 'iterations', label: 'Total Iterations',
              hint: `1 – ${maxIter.toLocaleString()}`, min: 1, max: maxIter
            },
            {
              key: 'concurrency', label: 'Concurrency (threads)',
              hint: `1 – ${maxConc}`, min: 1, max: maxConc
            },
            {
              key: 'warmupIterations', label: 'Warmup Iterations',
              hint: 'Excluded from stats', min: 0, max: form.iterations - 1
            },
          ].map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
              <input
                type="number"
                value={form[f.key]}
                min={f.min}
                max={f.max}
                disabled={running}
                onChange={e => field(f.key, Math.max(f.min, Math.min(f.max, parseInt(e.target.value) || f.min)))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono
                           focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400
                           disabled:bg-gray-50 disabled:text-gray-400"
              />
              <p className="text-xs text-gray-400 mt-1">{f.hint}</p>
            </div>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
            <XCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Run button */}
        <button
          onClick={handleRun}
          disabled={running}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-300
                     text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
        >
          {running ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Running…
            </>
          ) : (
            <>
              <Play className="w-4 h-4" />
              Run Test
            </>
          )}
        </button>
      </div>

      {/* Results */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-indigo-500" />
            Results
            {results.length > 0 && (
              <span className="text-xs text-gray-400 font-normal">({results.length} runs)</span>
            )}
          </div>
          {results.length > 0 && (
            <button
              onClick={() => perfTestApi.listResults(20).then(setResults)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <RotateCcw className="w-3 h-3" />
              Refresh
            </button>
          )}
        </div>

        {results.length === 0 ? (
          <div className="bg-white border border-dashed border-gray-300 rounded-xl p-10 text-center text-gray-400 text-sm">
            No runs yet. Configure and click <strong>Run Test</strong> above.
          </div>
        ) : (
          <div className="space-y-3">
            {results.map((r, i) => (
              <ResultCard key={r.runId ?? r.id ?? i} result={r} isLatest={i === 0} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
